import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@shared/hooks/useDebounce';
import { listTaskForms } from '../../api/listTaskFormsApiService';
import type { TaskFormListItem } from '../../types';

const ALLOWED_LIMITS = [10, 30, 50, 100] as const;
type AllowedLimit = (typeof ALLOWED_LIMITS)[number];

function isAllowedLimit(v: number): v is AllowedLimit {
  return (ALLOWED_LIMITS as readonly number[]).includes(v);
}

export function useFormList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [forms, setForms] = useState<TaskFormListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isNoFilterFetch, setIsNoFilterFetch] = useState(false);
  const [limitError, setLimitError] = useState('');

  const [page, setPage] = useState(() => {
    const v = parseInt(searchParams.get('page') ?? '1', 10);
    return isNaN(v) || v < 1 ? 1 : v;
  });
  const [limit, setLimit] = useState(() => {
    const v = parseInt(searchParams.get('limit') ?? '10', 10);
    return isAllowedLimit(v) ? v : 10;
  });
  const [search, setSearchState] = useState(() => {
    const v = (searchParams.get('search') ?? '').trim();
    return v.length > 200 ? v.substring(0, 200) : v;
  });

  const debouncedSearch = useDebounce(search, 500);
  const prevDebouncedRef = useRef(debouncedSearch);
  const isSyncingFromUrl = useRef(false);
  const lastWrittenRef = useRef<string | null>(null);

  // State → URL
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      return;
    }
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', limit.toString());
    if (search.trim()) {
      params.set('search', search.trim());
    }
    const str = params.toString();
    if (str === lastWrittenRef.current) {
      return;
    }
    lastWrittenRef.current = str;
    setSearchParams(params, { replace: true });
  }, [page, limit, search, setSearchParams]);

  // URL → State (back/forward navigation)
  useEffect(() => {
    const str = new URLSearchParams(searchParams).toString();
    if (str === lastWrittenRef.current) {
      return;
    } // our own write, skip

    isSyncingFromUrl.current = true;
    const urlPage = parseInt(searchParams.get('page') ?? '1', 10);
    const urlLimit = parseInt(searchParams.get('limit') ?? '10', 10);
    const urlSearch = (searchParams.get('search') ?? '').trim();

    setPage(isNaN(urlPage) || urlPage < 1 ? 1 : urlPage);
    setLimit(isAllowedLimit(urlLimit) ? urlLimit : 10);
    setSearchState(
      urlSearch.length > 200 ? urlSearch.substring(0, 200) : urlSearch
    );

    requestAnimationFrame(() => {
      isSyncingFromUrl.current = false;
    });
  }, [searchParams]);

  // Reset page when debounced search changes
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevDebouncedRef.current = debouncedSearch;
      return;
    }
    if (prevDebouncedRef.current.trim() !== debouncedSearch.trim()) {
      prevDebouncedRef.current = debouncedSearch;
      if (page !== 1) {
        setPage(1);
      }
    }
  }, [debouncedSearch, page]);

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const trimmed = debouncedSearch.trim();
      const result = await listTaskForms({
        page,
        limit,
        ...(trimmed && { search: trimmed }),
      });

      if (!result.success || !result.data) {
        setForms([]);
        setTotalCount(0);
        return;
      }

      const { data: items, pagination } = result.data;

      if (pagination.totalCount > 0 && page > pagination.totalPages) {
        setPage(pagination.totalPages);
        return;
      }

      setForms(items);
      setTotalCount(pagination.totalCount);
      setIsNoFilterFetch(!trimmed);
    } catch {
      setForms([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const setSearch = useCallback(
    (newSearch: string) => {
      if (newSearch.length > 200) {
        return;
      }
      if (!newSearch.trim() && search.trim()) {
        setLoading(true);
      }
      setSearchState(newSearch);
    },
    [search]
  );

  const handleLimitChange = useCallback((newLimit: number) => {
    if (!isAllowedLimit(newLimit)) {
      setLimitError('Dozwolone wartości: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const isSearchClearing = !search.trim() && debouncedSearch.trim() !== '';

  return {
    forms,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing,
    setSearch,
    limitError,
    handleLimitChange,
    refetch: fetchForms,
  };
}
